import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import baseFetchWithAuth from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import { makeSenseMatcher } from '../utils/gpsSense';
import { isDraftIssue } from '../utils/gpsStatusCategories';
import { GPS_CUSTOM_FILTERS } from '../constants/gpsFilters';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const GPSContext = createContext();

const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  const start = new Date(now);
  start.setDate(now.getDate() + diffToMonday);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

export function GPSProvider({ children }) {
  const initial = getWeekRange();
  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [periodType, setPeriodType] = useState('week');

  const [previousStartDate, setPreviousStartDate] = useState(initial.start);
  const [previousEndDate, setPreviousEndDate] = useState(initial.end);

  // Global "Afficher" sense: drives page composition (flux vs encours).
  // 'all' | 'received' | 'resolved' | 'ongoing'. This is a presentation/composition
  // concern only — it does not change the data fetched.
  const [sense, setSense] = useState('all');

  // Project selection
  const [projets, setProjets] = useState([]);
  const [selectedProjet, setSelectedProjet] = useState(null);
  const [lastSyncDate, setLastSyncDate] = useState(null);

  // issues
  const [issues, setIssues] = useState([]);
  const [previousIssues, setPreviousIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(false);

  const [filteredIssues, setFilteredIssues] = useState([]);
  const [filteredPreviousIssues, setFilteredPreviousIssues] = useState([]);

  // Filters options
  const [shopSigns, setShopSigns] = useState([]);
  const [assets, setAssets] = useState([]);
  const [tierings, setTierings] = useState([]);
  const [problems, setProblems] = useState([]);
  const [status, setStatus] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [furnitures, setFurnitures] = useState([]);
  const [brands, setBrands] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [shops, setShops] = useState([]);

  // Selected filters
  const [selectedShopSigns, setSelectedShopSigns] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedTierings, setSelectedTierings] = useState([]);
  const [selectedProblems, setSelectedProblems] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [selectedFurnitures, setSelectedFurnitures] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedRequestTypes, setSelectedRequestTypes] = useState([]);
  const [selectedShops, setSelectedShops] = useState([]);
  const [selectedCustomFilters, setSelectedCustomFilters] = useState([]);
  const [totalBrands, setTotalBrands] = useState(0);
  const [totalTierings, setTotalTierings] = useState(0);

  useEffect(() => {
    const getFilteredData = (data, excludeType = null) => {
      const shouldApplyFilter = (filterType) => filterType !== excludeType;

      return data.filter(issue => {
        if (shouldApplyFilter('shopSigns') && selectedShopSigns.length > 0)
          if (!selectedShopSigns.some(s => s.value === issue.shopsign))
            return false;

        if (shouldApplyFilter('assets') && selectedAssets.length > 0)
          if (!selectedAssets.some(a => issue.assets && issue.assets.includes(a.value)))
            return false;

        if (shouldApplyFilter('tierings') && selectedTierings.length > 0)
          if (!selectedTierings.some(t => t.value === issue.tiering))
            return false;

        if (shouldApplyFilter('requestTypes') && selectedRequestTypes.length > 0)
          if (!selectedRequestTypes.some(t => t.value === issue.request_type))
            return false;

        if (shouldApplyFilter('problems') && selectedProblems.length > 0)
          if (!selectedProblems.some(p => issue.problems && issue.problems.includes(p.value)))
            return false;

        if (shouldApplyFilter('status') && selectedStatus.length > 0)
          if (!selectedStatus.some(s => s.value === issue.status))
            return false;

        if (shouldApplyFilter('sectors') && selectedSectors.length > 0)
          if (!selectedSectors.some(s => s.value === issue.sector))
            return false;

        if (shouldApplyFilter('furnitures') && selectedFurnitures.length > 0)
          if (!selectedFurnitures.some(f => f.value === issue.furniture))
            return false;

        if (shouldApplyFilter('brands') && selectedBrands.length > 0)
          if (!selectedBrands.some(b => b.value === issue.brand))
            return false;

        if (shouldApplyFilter('shops') && selectedShops.length > 0)
          if (!selectedShops.some(s => s.value === issue.intervention_point))
            return false;

        if (shouldApplyFilter('customFilters') && selectedCustomFilters.length > 0) {
          return selectedCustomFilters.every(filter => {
            switch (filter.value) {
              case GPS_CUSTOM_FILTERS.PARTIAL:
                return issue.partial_intervention === true;
              case GPS_CUSTOM_FILTERS.REQUEST_COMPLIANT:
                return issue.request_compliant === true;
              case GPS_CUSTOM_FILTERS.REQUEST_NON_COMPLIANT:
                return issue.request_compliant === false;
              case GPS_CUSTOM_FILTERS.INTERVENTION_COMPLIANT:
                return issue.intervention_compliant === true;
              case GPS_CUSTOM_FILTERS.INTERVENTION_NON_COMPLIANT:
                return issue.intervention_compliant === false;
              case GPS_CUSTOM_FILTERS.OUT_OF_DATE:
                return issue.out_of_time === true;
              default:
                return true;
            }
          });
        }

        return true;
      });
    };

    // 1. Set global filtered issues (no exclusions)
    const filtered = getFilteredData(issues);
    const filteredPrevious = getFilteredData(previousIssues);
    setFilteredIssues(filtered);
    setFilteredPreviousIssues(filteredPrevious);

    // The "Afficher" sense participates in the dynamic cross-filtering: available
    // options reflect only the issues matching the current sense (flux vs encours).
    // It scopes the OPTION lists/counts, not the page data (KPIs partition on their own).
    const matchesSense = makeSenseMatcher(sense, { startDate, endDate });
    const optionData = (excludeType) => getFilteredData(issues, excludeType).filter(matchesSense);

    // 2. Compute available options for each filter type (excluding itself from filtering)
    // This allows seeing other options when one is selected
    const shopsignsData = optionData('shopSigns');
    setShopSigns(Array.from(new Set(shopsignsData.map(issue => issue.shopsign).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const assetsData = optionData('assets');
    setAssets(Array.from(new Set(assetsData.flatMap(issue => issue.assets || []).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const tieringsData = optionData('tierings');
    setTierings(Array.from(new Set(tieringsData.map(issue => issue.tiering).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const problemsData = optionData('problems');
    setProblems(Array.from(new Set(problemsData.flatMap(issue => issue.problems || []).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    // Drafts are excluded almost everywhere, so "Brouillon" is dropped from the
    // global Statut filter. Drafts stay reachable locally (Secteurs column filter,
    // Tickets per-column filter).
    const statusData = optionData('status');
    setStatus(Array.from(new Set(statusData.filter(issue => !isDraftIssue(issue)).map(issue => issue.status).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const sectorsData = optionData('sectors');
    setSectors(Array.from(new Set(sectorsData.map(issue => issue.sector).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const furnituresData = optionData('furnitures');
    setFurnitures(Array.from(new Set(furnituresData.map(issue => issue.furniture).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const brandsData = optionData('brands');
    setBrands(Array.from(new Set(brandsData.map(issue => issue.brand).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const requestTypesData = optionData('requestTypes');
    setRequestTypes(Array.from(new Set(requestTypesData.map(issue => issue.request_type).filter(v => v !== null && v !== ''))).map(v => ({ value: v, label: v })));

    const shopsData = optionData('shops');
    const shopsMap = new Map();
    shopsData.forEach(issue => {
      const store = issue.intervention_point;
      if (store?.length > 0 && !shopsMap.has(store)) {
        const enhancedLabel = issue.client_shop_id ? `${issue.client_shop_id} - ${store}` : null;
        shopsMap.set(store, { value: store, label: store, enhancedLabel });
      }
    });
    setShops(Array.from(shopsMap.values()));

    // Total counts are based on raw data usually, or filtered?
    // In previous code: setTotalBrands used issues (raw).
    setTotalBrands(Array.from(new Set(issues.map(issue => issue.brand).filter(brand => brand !== null && brand !== ''))).length);
    setTotalTierings(Array.from(new Set(issues.map(issue => issue.tiering).filter(tiering => tiering !== null && tiering !== ''))).length);

  }, [issues, previousIssues, sense, startDate, endDate, selectedShopSigns, selectedAssets, selectedTierings, selectedProblems, selectedStatus, selectedSectors, selectedFurnitures, selectedBrands, selectedRequestTypes, selectedShops, selectedCustomFilters]);

  useEffect(() => {
    const fetchIssues = async () => {
      if (!selectedProjet?.value || !startDate || !endDate) return;
      setIssuesLoading(true);

      try {
        const formatDate = (date) => {
          return dayjs(date).format('YYYY-MM-DD');
        };

        const resp = await baseFetchWithAuth(`api/dwh/gps/${encodeURIComponent(selectedProjet?.value)}/issues?startDate=${formatDate(startDate)}&endDate=${formatDate(endDate)}`);
        setIssues(resp.current || []);
        setPreviousIssues(resp.previous || []);


        setPreviousStartDate(new Date(resp.metadata.previousPeriod.startDate));
        setPreviousEndDate(new Date(resp.metadata.previousPeriod.endDate));
      } catch (e) {
        console.log('Failed to fetch GPS issues');
        setIssues([]);
        setPreviousIssues([]);
      } finally {
        setIssuesLoading(false);
      }
    };
    fetchIssues();
  }, [selectedProjet, startDate, endDate]);

  useEffect(() => {
    const fetchLastSyncDate = async () => {
      if (!selectedProjet?.value) {
        setLastSyncDate(null);
        return;
      }

      try {
        const resp = await baseFetchWithAuth(`api/dwh/gps/${encodeURIComponent(selectedProjet?.value)}/last-sync`);
        setLastSyncDate(resp.lastSyncDate);
      } catch (e) {
        console.log('Failed to fetch last sync date');
        setLastSyncDate(null);
      }
    };
    fetchLastSyncDate();
  }, [selectedProjet]);

  // Load the list of available projects once, so the project selector can live
  // anywhere (sidebar) independently of the filters card.
  useEffect(() => {
    const loadProjets = async () => {
      try {
        const resp = await baseFetchWithAuth(`${API_BASE_URL}/api/dwh/gps/projects`);
        const options = (resp || []).map((r) => ({ value: r.KEY, label: r.NAME })).filter((o) => !!o.value);
        setProjets(options);
      } catch (e) {
        console.log('Failed to load GPS projets');
        setProjets([]);
      }
    };
    loadProjets();
  }, []);


  // Sense-scoped dataset: gps.issues (fully user-filtered) narrowed by the
  // "Afficher" sense. Consumed by tabs that should react to the sense (Tickets).
  // gps.issues itself stays sense-agnostic (Général partitions it on its own).
  const sensedIssues = useMemo(
    () => filteredIssues.filter(makeSenseMatcher(sense, { startDate, endDate })),
    [filteredIssues, sense, startDate, endDate]
  );

  // Général-scoped datasets: drafts ("Brouillon") are excluded from the Général
  // tab analytics. They stay in `issues` / `sensedIssues` for Secteurs & Tickets.
  const summaryIssues = useMemo(() => filteredIssues.filter((i) => !isDraftIssue(i)), [filteredIssues]);
  const summarySensedIssues = useMemo(() => sensedIssues.filter((i) => !isDraftIssue(i)), [sensedIssues]);

  const value = {
    startDate,
    setStartDate,
    endDate,
    previousStartDate,
    previousEndDate,
    setEndDate,
    periodType,
    setPeriodType,
    sense,
    setSense,
    projets,
    setProjets,
    selectedProjet,
    setSelectedProjet,
    lastSyncDate,
    issues: filteredIssues,
    sensedIssues,
    summaryIssues,
    summarySensedIssues,
    setIssues,
    previousIssues: filteredPreviousIssues,
    setPreviousIssues,
    issuesLoading,
    setIssuesLoading,
    shopSigns,
    setShopSigns,
    assets,
    setAssets,
    problems,
    setProblems,
    selectedProblems,
    setSelectedProblems,
    sectors,
    setSectors,
    selectedSectors,
    setSelectedSectors,
    selectedShopSigns,
    setSelectedShopSigns,
    selectedAssets,
    setSelectedAssets,
    status,
    setStatus,
    selectedStatus,
    setSelectedStatus,
    tierings,
    setTierings,
    selectedTierings,
    setSelectedTierings,
    furnitures,
    setFurnitures,
    selectedFurnitures,
    setSelectedFurnitures,
    brands,
    setBrands,
    selectedBrands,
    setSelectedBrands,
    totalBrands,
    totalTierings,
    requestTypes,
    setRequestTypes,
    selectedRequestTypes,
    setSelectedRequestTypes,
    shops,
    setShops,
    selectedShops,
    setSelectedShops,
    selectedCustomFilters,
    setSelectedCustomFilters,
  };

  return (
    <GPSContext.Provider value={value}>
      {children}
    </GPSContext.Provider>
  );
}

export function useGPS() {
  const context = useContext(GPSContext);

  if (!context)
    throw new Error('useGPS must be used within a GPSProvider');

  return context;
}

